import { CONFIG_PANEL_KEYS } from '@zeus/shared';
import { COMPONENT_KEY } from '../sections/Commons/enums';

export type OnTextSelectCallback = (
  data: {
    text: string;
    range: { start: number; end: number };
    currentStyles?: Record<string, unknown>;
    dataKey: string;
  } | null
) => void;

export const createBuilderClickHandler = (
  onTextSelect: OnTextSelectCallback | undefined,
  dataKey: string
): ((e?: React.MouseEvent) => void) | undefined => {
  if (!onTextSelect) return undefined;

  return (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onTextSelect({
      dataKey,
      text: '',
      range: { start: 0, end: 0 },
      currentStyles: {},
    });
  };
};

export const triggerTextSelect = (onTextSelect: OnTextSelectCallback | undefined, dataKey: string): void => {
  if (onTextSelect) {
    onTextSelect({
      dataKey,
      text: '',
      range: { start: 0, end: 0 },
      currentStyles: {},
    });
  }
};

export const createButtonClickHandler = (
  onTextSelect: OnTextSelectCallback | undefined
): ((dataKey: string) => void) | undefined => {
  if (!onTextSelect) return undefined;

  return (dataKey: string) => {
    onTextSelect({
      dataKey,
      text: '',
      range: { start: 0, end: 0 },
      currentStyles: {},
    });
  };
};

const matchHeadingText = (key: string): boolean =>
  key === 'heading.text' || (key.includes('heading') && !key.includes('subheading'));

const matchSubheadingText = (key: string): boolean =>
  key === 'subHeading.text' || key === 'subheading.text' || key.includes('subheading') || key.includes('subHeading');

const matchBodyOrDescription = (key: string): boolean =>
  key === 'bodyText.text' || key === 'description.text' || key.includes('bodyText') || key.includes('description');

const matchPrimaryButton = (key: string): boolean =>
  key === 'button.text' || key === 'button.label' || (key.includes('button') && !key.includes('secondaryButton'));

const matchSecondaryButton = (key: string): boolean =>
  key === 'secondaryButton.text' || key === 'secondaryButton.label' || key.includes('secondaryButton');

const matchBackgroundNoMedia = (key: string): boolean =>
  key === 'background' || (key.includes('background') && !key.includes('image') && !key.includes('video'));

const createSimpleBackgroundMatcher = (includeImagesKey = false) => {
  return (key: string): boolean =>
    key === 'background' ||
    key === 'image' ||
    key === 'video' ||
    key.includes('background') ||
    (includeImagesKey && key.includes('images'));
};
const matchHeadingExcluding = (key: string, exclusions: string[] = []): boolean =>
  matchHeadingText(key) && !exclusions.some((ex) => key.includes(ex));

const matchSubheadingExcluding = (key: string, exclusions: string[] = []): boolean =>
  matchSubheadingText(key) && !exclusions.some((ex) => key.includes(ex));

const matchDescriptionExcluding = (key: string, exclusions: string[] = []): boolean =>
  (key === 'description.text' || key.includes('description')) && !exclusions.some((ex) => key.includes(ex));

type SelectionRule = {
  matcher: (key: string) => boolean;
  panel: CONFIG_PANEL_KEYS;
  expandedSection: string | null;
  captureSelection: boolean;
};

type TextRuleKind = 'heading' | 'subheading' | 'bodyOrDescription' | 'description';

type TextRuleConfig = {
  type: TextRuleKind;
  section: string;
  panel: CONFIG_PANEL_KEYS;
  captureSelection: boolean;
  exclude?: string[];
};

const TEXT_RULE_CONFIG: Partial<Record<string, TextRuleConfig[]>> = {
  [COMPONENT_KEY.HERO]: [
    {
      type: 'heading',
      section: 'HEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'SUBHEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
      exclude: ['button'],
    },
  ],
  [COMPONENT_KEY.GIFT_CARDS]: [
    {
      type: 'heading',
      section: 'GIFT_CARDS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'GIFT_CARDS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'GIFT_CARDS_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.HOW_IT_WORKS]: [
    {
      type: 'heading',
      section: 'HOW_IT_WORKS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'HOW_IT_WORKS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.LOYALTY_FORM]: [
    {
      type: 'heading',
      section: 'LOYALTY_FORM_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'LOYALTY_FORM_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'LOYALTY_FORM_BODY',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.TIERS_POINTS]: [
    {
      type: 'heading',
      section: 'TIERS_POINTS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'TIERS_POINTS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'TIERS_POINTS_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.ACCESSIBILITY]: [
    {
      type: 'heading',
      section: 'ACCESSIBILITY_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'SUBHEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'ACCESSIBILITY_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['button'],
    },
  ],
  [COMPONENT_KEY.IMAGE_GALLERY]: [
    {
      type: 'heading',
      section: 'IMAGE_GALLERY_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'IMAGE_GALLERY_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'description',
      section: 'IMAGE_GALLERY_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.ABOUT_US]: [
    {
      type: 'heading',
      section: 'ABOUT_US_HEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'ABOUT_US_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'ABOUT_US_BODY',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.LOCATION]: [
    {
      type: 'heading',
      section: 'LOCATION_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'LOCATION_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'LOCATION_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.DEALS]: [
    {
      type: 'heading',
      section: 'DEALS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['deal'],
    },
    {
      type: 'subheading',
      section: 'DEALS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['deal'],
    },
    {
      type: 'description',
      section: 'DEALS_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['deal'],
    },
  ],
  [COMPONENT_KEY.SPOTLIGHTS]: [
    {
      type: 'heading',
      section: 'SPOTLIGHTS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['spotlight'],
    },
    {
      type: 'subheading',
      section: 'SPOTLIGHTS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['spotlight'],
    },
    {
      type: 'description',
      section: 'SPOTLIGHTS_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['spotlight'],
    },
  ],
  [COMPONENT_KEY.FEATURED_MENU]: [
    {
      type: 'heading',
      section: 'FEATURED_MENU_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['menuItem'],
    },
    {
      type: 'subheading',
      section: 'FEATURED_MENU_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['menuItem'],
    },
    {
      type: 'description',
      section: 'FEATURED_MENU_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['menuItem'],
    },
  ],
  [COMPONENT_KEY.CUSTOM_SECTION]: [
    {
      type: 'heading',
      section: 'CUSTOM_SECTION_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'CUSTOM_SECTION_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'description',
      section: 'CUSTOM_SECTION_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.CALL_TO_ACTION]: [
    {
      type: 'heading',
      section: 'CALL_TO_ACTION_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'CALL_TO_ACTION_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
    {
      type: 'bodyOrDescription',
      section: 'CALL_TO_ACTION_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.INFO_CARDS]: [
    {
      type: 'heading',
      section: 'INFO_CARDS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
      exclude: ['cardHeading'],
    },
    {
      type: 'subheading',
      section: 'INFO_CARDS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
  [COMPONENT_KEY.TESTIMONIALS]: [
    {
      type: 'heading',
      section: 'TESTIMONIALS_HEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'subheading',
      section: 'TESTIMONIALS_SUBHEADING',
      panel: CONFIG_PANEL_KEYS.INLINE_TEXT_STYLES,
      captureSelection: true,
    },
    {
      type: 'description',
      section: 'TESTIMONIALS_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: true,
    },
  ],
};

const buildTextRules = (configs?: TextRuleConfig[]): SelectionRule[] => {
  if (!configs) return [];

  return configs.map(({ type, section, panel, captureSelection, exclude }) => {
    let matcher: (key: string) => boolean;

    switch (type) {
      case 'heading':
        matcher = (key: string) => matchHeadingExcluding(key, exclude);
        break;
      case 'subheading':
        matcher = (key: string) => matchSubheadingExcluding(key, exclude);
        break;
      case 'bodyOrDescription':
        matcher = (key: string) => matchBodyOrDescription(key) && !(exclude ?? []).some((ex) => key.includes(ex));
        break;
      case 'description':
      default:
        matcher = (key: string) => matchDescriptionExcluding(key, exclude);
        break;
    }

    return {
      matcher,
      panel,
      expandedSection: section,
      captureSelection,
    };
  });
};

type MenuItemField = 'name' | 'image' | 'description' | 'price' | 'card';

type MenuItemRuleConfig = {
  field: MenuItemField;
  section: string;
  panel: CONFIG_PANEL_KEYS;
  captureSelection: boolean;
  equals?: string[];
};

const MENU_ITEM_RULE_CONFIG: Partial<Record<string, MenuItemRuleConfig[]>> = {
  [COMPONENT_KEY.FEATURED_MENU]: [
    {
      field: 'name',
      section: 'FEATURED_MENU_ITEM_NAME',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.name', 'menuItem.itemName'],
    },
    {
      field: 'image',
      section: 'FEATURED_MENU_ITEM_IMAGE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.image', 'menuItem.itemPicture'],
    },
    {
      field: 'description',
      section: 'FEATURED_MENU_ITEM_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.description', 'menuItem.itemDescription'],
    },
    {
      field: 'price',
      section: 'FEATURED_MENU_ITEM_PRICE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.price', 'menuItem.itemPrice'],
    },
    {
      field: 'card',
      section: 'FEATURED_MENU_ITEM_CARD_BORDERS',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.card', 'menuItem.border', 'menuItem.cardBorder'],
    },
  ],
  [COMPONENT_KEY.MENU]: [
    {
      field: 'name',
      section: 'MENU_ITEM_TITLE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.name', 'menuItem.itemName'],
    },
    {
      field: 'image',
      section: 'MENU_ITEM_IMAGE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.image'],
    },
    {
      field: 'description',
      section: 'MENU_ITEM_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.description'],
    },
    {
      field: 'price',
      section: 'MENU_ITEM_PRICE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.price'],
    },
  ],
  [COMPONENT_KEY.TIERS_POINTS]: [
    {
      field: 'name',
      section: 'TIERS_POINTS_ITEM_NAME',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.name', 'menuItem.itemName'],
    },
    {
      field: 'description',
      section: 'TIERS_POINTS_ITEM_DESCRIPTION',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.description'],
    },
    {
      field: 'price',
      section: 'TIERS_POINTS_ITEM_PRICE',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
      equals: ['menuItem.price'],
    },
  ],
};

const buildMenuItemRules = (configs?: MenuItemRuleConfig[]): SelectionRule[] => {
  if (!configs) return [];

  return configs.map(({ field, section, panel, captureSelection, equals }) => {
    const equalsSet = new Set(equals ?? []);

    let matcher: (key: string) => boolean;

    switch (field) {
      case 'name':
        matcher = (key: string) =>
          equalsSet.has(key) || (key.includes('menuItem') && key.includes('name') && !key.includes('description'));
        break;
      case 'image':
        matcher = (key: string) =>
          equalsSet.has(key) ||
          (key.includes('menuItem') &&
            (key.includes('image') || key.includes('picture')) &&
            !key.includes('background'));
        break;
      case 'description':
        matcher = (key: string) =>
          equalsSet.has(key) || (key.includes('menuItem') && key.includes('description') && !key.includes('name'));
        break;
      case 'price':
        matcher = (key: string) => equalsSet.has(key) || (key.includes('menuItem') && key.includes('price'));
        break;
      case 'card':
      default:
        matcher = (key: string) =>
          equalsSet.has(key) || (key.includes('menuItem') && (key.includes('card') || key.includes('border')));
        break;
    }

    return {
      matcher,
      panel,
      expandedSection: section,
      captureSelection,
    };
  });
};

type LocationField = 'addressHeading' | 'address' | 'map' | 'serviceHeading' | 'serviceHours';

type LocationRuleConfig = {
  field: LocationField;
  section: string;
  panel: CONFIG_PANEL_KEYS;
  captureSelection: boolean;
};

const LOCATION_RULE_CONFIG: Partial<Record<string, LocationRuleConfig[]>> = {
  [COMPONENT_KEY.LOCATION]: [
    {
      field: 'addressHeading',
      section: 'LOCATION_ADDRESS_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
    },
    {
      field: 'address',
      section: 'LOCATION_ADDRESS',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
    },
    {
      field: 'map',
      section: 'LOCATION_MAP',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
    },
    {
      field: 'serviceHeading',
      section: 'LOCATION_SERVICE_HEADING',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
    },
    {
      field: 'serviceHours',
      section: 'LOCATION_SERVICE_HOURS',
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      captureSelection: false,
    },
  ],
};

const buildLocationRules = (configs?: LocationRuleConfig[]): SelectionRule[] => {
  if (!configs) return [];

  return configs.map(({ field, section, panel, captureSelection }) => {
    let matcher: (key: string) => boolean;

    switch (field) {
      case 'addressHeading':
        matcher = (key: string) =>
          key === 'location.addressHeading' || key.includes('addressHeading') || key.includes('ADDRESS_HEADING');
        break;
      case 'address':
        matcher = (key: string) =>
          key === 'location.address' ||
          key === 'address' ||
          key.includes('address') ||
          key.includes('LOCATION_ADDRESS');
        break;
      case 'map':
        matcher = (key: string) =>
          key.includes('map') || key.includes('latitude') || key.includes('longitude') || key.includes('Map');
        break;
      case 'serviceHeading':
        matcher = (key: string) =>
          key === 'location.serviceHoursHeading' ||
          key.includes('serviceHoursHeading') ||
          key.includes('SERVICE_HEADING');
        break;
      case 'serviceHours':
      default:
        matcher = (key: string) =>
          key === 'location.serviceHours' ||
          key.includes('serviceHours') ||
          key.includes('HOURS_HEADING') ||
          key.includes('HOURS');
        break;
    }

    return {
      matcher,
      panel,
      expandedSection: section,
      captureSelection,
    };
  });
};

// Helper function to create button rules
const createButtonRule = (
  type: 'primary' | 'secondary' | 'tertiary',
  section: string,
  panel: CONFIG_PANEL_KEYS = CONFIG_PANEL_KEYS.EDIT_SECTION
): SelectionRule => {
  const matchers = {
    primary: matchPrimaryButton,
    secondary: matchSecondaryButton,
    tertiary: (key: string) =>
      key === 'tertiaryButton.text' || key === 'tertiaryButton.label' || key.includes('tertiaryButton'),
  };

  return {
    matcher: matchers[type],
    panel,
    expandedSection: section,
    captureSelection: false,
  };
};

// Helper function to create background rules
const createBackgroundRule = (section: string, includeVideo = true, includeCarousel = false): SelectionRule => {
  return {
    matcher: (key) => {
      const baseMatch = key === 'background' || (key.includes('background') && !key.includes('image'));
      const videoMatch = includeVideo && (key === 'video' || key.includes('video'));
      const carouselMatch = includeCarousel && (key === 'carousel' || key.includes('carousel'));
      return baseMatch || videoMatch || carouselMatch;
    },
    panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
    expandedSection: section,
    captureSelection: false,
  };
};

// Helper function to create image rules
const createImageRule = (section: string, includeSecondary = true, excludeCarousel = true): SelectionRule => {
  return {
    matcher: (key) => {
      const backgroundExclude = !key.includes('background');
      const carouselExclude = !excludeCarousel || !key.includes('carousel');
      const primaryMatch = key === 'image' || (key.includes('image') && backgroundExclude && carouselExclude);
      const secondaryMatch = includeSecondary && (key === 'secondaryImage' || key.includes('secondaryImage'));
      return primaryMatch || secondaryMatch;
    },
    panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
    expandedSection: section,
    captureSelection: false,
  };
};

export const COMPONENT_SELECTION_CONFIG: Record<string, SelectionRule[]> = {
  [COMPONENT_KEY.HERO]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.HERO]),
    createButtonRule('primary', 'PRIMARY_BUTTON'),
    createButtonRule('secondary', 'SECONDARY_BUTTON'),
    createBackgroundRule('BACKGROUND', true, true),
    createImageRule('IMAGE', true, true),
  ],
  [COMPONENT_KEY.GIFT_CARDS]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.GIFT_CARDS]),
    createButtonRule('primary', 'GIFT_CARDS_BUTTON'),
  ],
  [COMPONENT_KEY.HOW_IT_WORKS]: [
    {
      matcher: (key) => key === 'backgroundImage' || matchBackgroundNoMedia(key),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'HOW_IT_WORKS_BACKGROUND',
      captureSelection: false,
    },
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.HOW_IT_WORKS]),
    {
      matcher: (key) => key === 'stepTitle' || key.includes('stepTitle'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'HOW_IT_WORKS_STEP_TITLE',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'stepDescription' || key.includes('stepDescription'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'HOW_IT_WORKS_STEP_DESCRIPTION',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.LOYALTY_FORM]: [
    {
      matcher: (key) => key === 'backgroundImage' || matchBackgroundNoMedia(key),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'LOYALTY_FORM_BACKGROUND',
      captureSelection: false,
    },
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.LOYALTY_FORM]),
    createImageRule('LOYALTY_FORM_IMAGE', true, false),
    createButtonRule('primary', 'LOYALTY_FORM_BUTTON'),
  ],
  [COMPONENT_KEY.TIERS_POINTS]: [
    {
      matcher: (key) => key === 'backgroundImage' || matchBackgroundNoMedia(key),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'TIERS_POINTS_BACKGROUND',
      captureSelection: false,
    },
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.TIERS_POINTS]),
    ...buildMenuItemRules(MENU_ITEM_RULE_CONFIG[COMPONENT_KEY.TIERS_POINTS]),
  ],
  // Treat Accessibility component same as Hero for now based on original logic
  [COMPONENT_KEY.ACCESSIBILITY]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.ACCESSIBILITY]),
    createButtonRule('primary', 'PRIMARY_BUTTON'),
    createButtonRule('secondary', 'SECONDARY_BUTTON'),
    createBackgroundRule('BACKGROUND', true, true),
    createImageRule('IMAGE', true, true),
  ],
  [COMPONENT_KEY.HEADER]: [
    {
      matcher: (key) => key === 'button.text' || key === 'button.label' || key.includes('button'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'HEADER_BUTTON',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'navlink' || key.includes('navlink'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'HEADER_NAVIGATION',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.FOOTER]: [
    {
      matcher: (key) =>
        key === 'background' || (key.includes('background') && !key.includes('image') && !key.includes('video')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FOOTER_BACKGROUND',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'contactTitle.text' || (key.includes('contactTitle') && key.includes('text')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FOOTER_HEADING',
      captureSelection: true,
    },
    {
      matcher: (key) => key === 'socials' || key.includes('social'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FOOTER_SOCIALS',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'address' || key.includes('address'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FOOTER_ADDRESS',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.FEATURED_MENU]: [
    createBackgroundRule('FEATURED_MENU_BACKGROUND', false, false),
    createButtonRule('primary', 'FEATURED_MENU_BUTTON'),
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.FEATURED_MENU]),
    ...buildMenuItemRules(MENU_ITEM_RULE_CONFIG[COMPONENT_KEY.FEATURED_MENU]),
  ],
  [COMPONENT_KEY.MENU]: [
    {
      matcher: (key) =>
        key === 'heading.text' ||
        (key.includes('heading') &&
          !key.includes('subheading') &&
          !key.includes('category') &&
          !key.includes('menuItem')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_HEADING',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        (key === 'description.text' ||
          key === 'subHeading.text' ||
          key === 'subheading.text' ||
          key.includes('subheading') ||
          key.includes('subHeading') ||
          key.includes('description')) &&
        !key.includes('menuItem') &&
        !key.includes('category'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_SUBHEADING',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'categoryHeading.text' ||
        key === 'categoryHeading' ||
        (key.includes('categoryHeading') && !key.includes('subCategory')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_CATEGORY_HEADING',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'subCategoryHeading.text' ||
        key === 'subCategoryHeading' ||
        key.includes('subCategoryHeading') ||
        (key.includes('subCategory') && key.includes('heading')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_SUBCATEGORY_HEADING',
      captureSelection: true,
    },
    ...buildMenuItemRules(MENU_ITEM_RULE_CONFIG[COMPONENT_KEY.MENU]),
    {
      matcher: (key) => key === 'itemPopup.name' || key.includes('itemPopup.name'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_ITEM_POPUP_NAME',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'itemPopup.price' || key.includes('itemPopup.price'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_ITEM_POPUP_PRICE',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'itemPopup.description' || key.includes('itemPopup.description'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_ITEM_POPUP_DESCRIPTION',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'itemPopup.modifiers' || key.includes('itemPopup.modifiers'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_ITEM_POPUP_MODIFIERS',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'itemPopup' || key.startsWith('itemPopup.') || key.includes('itemPopup'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'MENU_ITEM_POPUP',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.IMAGE_GALLERY]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.IMAGE_GALLERY]),
    {
      matcher: (key) => key === 'background' || key === 'video' || key.includes('background'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'IMAGE_GALLERY_BACKGROUND',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'images' || key.includes('images'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'IMAGE_GALLERY_IMAGES',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.ABOUT_US]: [
    {
      matcher: (key) => key === 'overline.text' || key.includes('overline'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'ABOUT_US_OVERLINE',
      captureSelection: true,
    },
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.ABOUT_US]),
    createButtonRule('secondary', 'ABOUT_US_SECONDARY_BUTTON'),
    createButtonRule('primary', 'ABOUT_US_BUTTON'),
    {
      matcher: (key) =>
        key === 'media' || key === 'mediaConfig' || (key.includes('media') && !key.includes('background')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'ABOUT_US_MEDIA',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'background' ||
        key === 'image' ||
        key === 'video' ||
        key.includes('background') ||
        key.includes('images'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'ABOUT_US_BACKGROUND',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.LOCATION]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.LOCATION]),
    {
      matcher: (key) => matchBackgroundNoMedia(key),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'LOCATION_BACKGROUND',
      captureSelection: false,
    },
    createButtonRule('primary', 'LOCATION_BUTTON'),
    ...buildLocationRules(LOCATION_RULE_CONFIG[COMPONENT_KEY.LOCATION]),
  ],
  [COMPONENT_KEY.DEALS]: [
    createBackgroundRule('DEALS_BACKGROUND', false, false),
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.DEALS]),
    createButtonRule('primary', 'DEALS_BUTTON'),
    {
      matcher: (key) => key === 'deal.name' || key === 'dealName' || (key.includes('deal') && key.includes('name')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'DEALS_DEAL_NAME',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'deal.type' || key === 'dealType' || (key.includes('deal') && key.includes('type')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'DEALS_DEAL_TYPE',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'deal.description' ||
        key === 'dealDescription' ||
        (key.includes('deal') && key.includes('description')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'DEALS_DEAL_DESCRIPTION',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'deal.availability' ||
        key === 'dealAvailability' ||
        (key.includes('deal') && key.includes('availability')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'DEALS_DEAL_AVAILABILITY',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.SPOTLIGHTS]: [
    createBackgroundRule('SPOTLIGHTS_BACKGROUND', false, false),
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.SPOTLIGHTS]),
    {
      matcher: (key) =>
        key === 'spotlight.heading' ||
        key === 'spotlightHeading' ||
        (key.includes('spotlight') && key.includes('heading') && !key.includes('description')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'SPOTLIGHTS_SPOTLIGHT_HEADING',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'spotlight.description' ||
        key === 'spotlightDescription' ||
        (key.includes('spotlight') && key.includes('description')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'SPOTLIGHTS_SPOTLIGHT_DESCRIPTION',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'spotlight.button' ||
        key === 'spotlightButton' ||
        (key.includes('spotlight') && key.includes('button')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'SPOTLIGHTS_SPOTLIGHT_BUTTON',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.CUSTOM_SECTION]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.CUSTOM_SECTION]),
    {
      matcher: (key) =>
        key === 'htmlContent' ||
        key === 'cssContent' ||
        key === 'jsContent' ||
        key === 'htmlCssJs' ||
        (key.includes('html') && key.includes('css')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CUSTOM_SECTION_HTML_CSS_JS',
      captureSelection: false,
    },
    createButtonRule('primary', 'CUSTOM_SECTION_BUTTON'),
    {
      matcher: createSimpleBackgroundMatcher(true),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CUSTOM_SECTION_BACKGROUND',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.CALL_TO_ACTION]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.CALL_TO_ACTION]),
    createButtonRule('primary', 'CALL_TO_ACTION_BUTTON'),
    createButtonRule('secondary', 'CALL_TO_ACTION_SECONDARY_BUTTON'),
    {
      matcher: createSimpleBackgroundMatcher(true),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CALL_TO_ACTION_BACKGROUND',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.FORM]: [
    {
      matcher: matchBackgroundNoMedia,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_BACKGROUND',
      captureSelection: false,
    },
    {
      matcher: matchHeadingText,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_HEADING',
      captureSelection: true,
    },
    {
      matcher: matchSubheadingText,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_SUBHEADING',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'bodyText.text' || key === 'paragraph.text' || key.includes('bodyText') || key.includes('paragraph'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_BODY',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'image' || key === 'image.value' || (key.includes('image') && !key.includes('background')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_IMAGE',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'description.text' || key.includes('description'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_DESCRIPTION',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'button.text' || key === 'button.label' || key.includes('button') || key.includes('submit'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_BUTTON',
      captureSelection: false,
    },
    {
      matcher: (key) => key.includes('field') || key.includes('input') || key.includes('form'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'FORM_FIELDS',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.CATERING_FORM]: [
    {
      matcher: matchBackgroundNoMedia,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_BACKGROUND',
      captureSelection: false,
    },
    {
      matcher: matchHeadingText,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_HEADING',
      captureSelection: true,
    },
    {
      matcher: matchSubheadingText,
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_SUBHEADING',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'bodyText.text' || key === 'paragraph.text' || key.includes('bodyText') || key.includes('paragraph'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_BODY',
      captureSelection: true,
    },
    {
      matcher: (key) =>
        key === 'image' || key === 'image.value' || (key.includes('image') && !key.includes('background')),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_ICON',
      captureSelection: false,
    },
    {
      matcher: (key) =>
        key === 'button.text' || key === 'button.label' || key.includes('button') || key.includes('submit'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_BUTTON',
      captureSelection: false,
    },
    {
      matcher: (key) => key === 'description.text' || key.includes('description'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_DESCRIPTION',
      captureSelection: true,
    },
    {
      matcher: (key) => key.includes('field') || key.includes('input') || key.includes('form'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'CATERING_FORM_FIELDS',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.INFO_CARDS]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.INFO_CARDS]),
    {
      matcher: (key) => key.includes('cardOverline'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_CARD_OVERLINE',
      captureSelection: true,
    },
    {
      matcher: (key) => key.includes('cardHeading'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_CARD_HEADING',
      captureSelection: true,
    },
    {
      matcher: (key) => key.includes('cardSubheading'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_CARD_SUBHEADING',
      captureSelection: true,
    },
    {
      matcher: (key) => key.includes('cardDescription'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_CARD_DESCRIPTION',
      captureSelection: true,
    },
    createButtonRule('secondary', 'INFO_CARDS_BUTTON'),
    createButtonRule('tertiary', 'INFO_CARDS_BUTTON'),
    createButtonRule('primary', 'INFO_CARDS_BUTTON'),
    {
      matcher: createSimpleBackgroundMatcher(),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_BACKGROUND',
      captureSelection: false,
    },
    {
      matcher: (key) => key.includes('card') || key.includes('icon') || key.includes('image'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'INFO_CARDS_CARDS',
      captureSelection: false,
    },
  ],
  [COMPONENT_KEY.TESTIMONIALS]: [
    ...buildTextRules(TEXT_RULE_CONFIG[COMPONENT_KEY.TESTIMONIALS]),
    {
      matcher: (key) => key.includes('testimonial.review') || key.includes('review') || key.includes('quote'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'TESTIMONIALS_ITEMS',
      captureSelection: false,
    },
    {
      matcher: (key) => key.includes('testimonial.customerName') || key.includes('customerName'),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'TESTIMONIALS_CUSTOMER_NAME',
      captureSelection: false,
    },
    {
      matcher: createSimpleBackgroundMatcher(),
      panel: CONFIG_PANEL_KEYS.EDIT_SECTION,
      expandedSection: 'TESTIMONIALS_BACKGROUND',
      captureSelection: false,
    },
  ],
};
